import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';

type Props = {
  containerRef: React.RefObject<HTMLElement>;
  onFiles: (files: File[]) => void;
};

export function TaskPanelDragOverlay({ containerRef, onFiles }: Props) {
  const [visible, setVisible] = useState(false);
  const dragCounterRef = useRef(0);

  useEffect(() => {
    const hasFiles = (e: DragEvent) =>
      Array.from(e.dataTransfer?.types ?? []).includes('Files');

    const isWithinContainer = (e: DragEvent) => {
      const container = containerRef.current;
      const target = e.target;
      return (
        !!container && target instanceof Node && container.contains(target)
      );
    };

    const onDragEnter = (e: DragEvent) => {
      if (!hasFiles(e) || !isWithinContainer(e)) return;
      dragCounterRef.current += 1;
      setVisible(true);
    };

    const onDragLeave = (e: DragEvent) => {
      if (!hasFiles(e) || !isWithinContainer(e)) return;
      dragCounterRef.current -= 1;

      if (dragCounterRef.current <= 0) {
        dragCounterRef.current = 0;
        setVisible(false);
      }
    };

    const onDragOver = (e: DragEvent) => {
      if (!hasFiles(e) || !isWithinContainer(e)) return;
      e.preventDefault();
    };

    const onDrop = (e: DragEvent) => {
      if (!hasFiles(e) || !isWithinContainer(e)) return;

      e.preventDefault();
      dragCounterRef.current = 0;
      setVisible(false);

      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length) onFiles(files);
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);

    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, [onFiles]);

  if (!visible || !containerRef.current) return null;

  return createPortal(
    <Box
      position='absolute'
      top={0}
      left={0}
      right={0}
      bottom={0}
      alignItems='center'
      justifyContent='center'
      style={{
        backgroundColor: 'rgba(220, 242, 236, 0.5)',
        pointerEvents: 'none',
        zIndex: 10,
      }}>
      <Box
        flexDirection='row'
        alignItems='center'
        px='m'
        py='xs'
        backgroundColor='white'
        style={{
          borderRadius: 40,
          boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
        }}>
        <Icon name='UploadCloud' color='black' variant='s' marginRight='xs' />
        <Text variant='webSmall' color='black'>
          Attach File
        </Text>
      </Box>
    </Box>,
    containerRef.current
  );
}
